import React, { useEffect, useState } from 'react';
import { useRouter } from 'next/router';
import ConfirmModal from '@components/common/confirmModal';
import { usePSTMaxApplyCount } from '@components/acca/test/query/useApplyCount';
import ContinueList from '@components/acca/test/list/continueList';
import AccaModal from '@components/acca/test/modals';
import { useTermsCheckNewMutation } from '@components/acca/test/query/useTerms';
import { useUserInfo } from '@repository/auth/useUserInfo';
import { getAccessToken } from '@repository/AccessToken';
import { useApplyHistoryList } from '@repository/acc/useAcca';
import { AccaModalType } from '@domain/constant/acca/AccaModalType';
import { JOBDA_SUPPORT_EMAIL } from '@domain/constant/contact';
import { TERMS } from '@domain/constant/termsType';
import { ContentRouteUtil } from '@utils/routeUtil';
import classnames from 'classnames/bind';
import styles from './accaTestList.module.scss';
import dynamic from 'next/dynamic';
import useKcbIdentity from '@utils/hooks/useKcbIdentity';

const ReactPlayer = dynamic(() => import('react-player') as any, { ssr: false }) as any;
const cx = classnames.bind(styles);

const AccaTestList = () => {
  const { pathname } = useRouter();
  const [modalState, setModalState] = useState<AccaModalType>(null);
  const { data: userInfo } = useUserInfo();
  const isLogin = !!userInfo?.id;
  const { data: maxApplyCount } = usePSTMaxApplyCount();
  const { data: applyHistoryData } = useApplyHistoryList(isLogin, { refetchOnWindowFocus: true });
  const chanceCount = applyHistoryData?.chanceCount ?? 0;
  const isDisabledBeginApplyBtn = chanceCount < 1; //처음부터 하기 버튼

  const { isVerification } = useKcbIdentity({
    onError: () => setModalState(null),
    onSuccess: () => setModalState('TEST_TERMS'),
  });
  const { mutateAsync: checkTermsMutateAsync } = useTermsCheckNewMutation();

  //처음부터 하기 버튼 이벤트
  const handelClickTestBtn = async () => {
    //본인 인증 체크
    if (!isVerification) {
      setModalState('VERIFICATION');
      return;
    }

    //약관 동의 여부 체크
    const { existsNewTermsAgree } = await checkTermsMutateAsync(TERMS.ACC_PRIVACY_POLICY);
    if (existsNewTermsAgree) {
      setModalState('TEST_TERMS');
      return;
    }

    ConfirmModal({
      size: 'xlarge',
      title: (
        <>
          처음부터 응시하기 시 응시 기회가 1회 차감됩니다.
          <br />
          처음부터 응시하시겠습니까?
        </>
      ),
      onConfirm: () => {
        //응시 기회 존재 여부 체크
        if (!applyHistoryData || applyHistoryData?.chanceCount === 0) {
          setModalState('NO_MORE_CHANCE');
          return;
        }

        setModalState('TEST_GUIDE');
      },
    });
  };

  useEffect(() => {
    //미 로그인 상태로 접근시 로그인 페이지로 이동
    if (!getAccessToken()) ContentRouteUtil.goLoginPage(pathname);
  }, []);

  return (
    <div className={cx('AccaGateArea')}>
      <div className={cx('visualArea')}>
        <div className={cx('visualWrap')}>
          <ReactPlayer
            url={'/video/acca/test/videoGate.mp4'}
            playing={true}
            muted={true}
            loop={true}
            width={'100%'}
            height={'100%'}
          />
        </div>
      </div>

      <div className={cx('infoArea')}>
        <div className={cx('infoInner')}>
          <div className={cx('titleArea')}>
            <strong className={cx('title')}>실전응시</strong>
            <em className={cx('subTitle')}>역검을 응시하면 역검 제출, 불러오기 등에 활용할 수 있습니다.</em>
            <p className={cx('desc')}>
              응시 결과는 <span className={cx('emphasis')}>[내 프로필]</span>에서 확인할 수 있으며,
              <br />
              기업별 응시는 응시 기회를 차감하지 않습니다.
            </p>
          </div>

          <div className={cx('btnArea')}>
            {/* 처음부터 응시하기 영역 */}
            <div className={cx('btnInner', 'first')}>
              <button
                className={cx('btn')}
                type="button"
                disabled={isDisabledBeginApplyBtn}
                onClick={handelClickTestBtn}
              >
                <div className={cx('textWrap')}>
                  <span className={cx('badge')}>{chanceCount}회 남음</span>처음부터 응시하기
                </div>
              </button>
              <div className={cx('notice')}>
                응시 기회는 한 달에 5회(제휴 회원은 연 120회) 제공되며, 매월 1일에 초기화됩니다.
              </div>
              <div className={cx('notice')}>
                응시에 문제가 생길 경우 <a href={`mailto:${JOBDA_SUPPORT_EMAIL}`}>{JOBDA_SUPPORT_EMAIL}</a>로
                문의해주세요.
              </div>
            </div>

            <div className={cx('scrollArea')}>
              <div className={cx('scrollInner')}>
                {/* 이어하기 영역 */}
                <div className={cx('btnInner', 'continue')}>
                  <ContinueList applyHistoryData={applyHistoryData} setModalState={setModalState} />
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <AccaModal modalState={modalState} setModalState={setModalState} />
    </div>
  );
};

export default AccaTestList;
